/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react';

import { Box, Button, Grid, LinearProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { DescriptionOutlined, ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { Close, Sync } from '@mui/icons-material';
import { Pagination } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import DatePicker from '../../components/common/DatePicker';
import ModalityMultiSelect from '../../components/common/ModalityMultiSelect';
import SearchInput from '../../components/common/SearchInput';
import StatusMultiSelect from '../../components/common/StatusMultiSelect';
import TypeMultiSelect from '../../components/common/TypeMultiSelect';
import CreateQualityObservationButton from '../../components/worklist/CreateQualityObservationButton';
import WorkFeedbackButton from '../../components/worklist/WorkFeedbackButton';
import WorklistItem from '../../components/worklist/WorklistItem';
import { ROLES } from '../../constants/general';
import { observationStatuses } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import { useWebSocketEvents } from '../../hooks/useWebSocketEvents';
import FacilityService from '../../services/facility.service';
import QualityObservationService from '../../services/quality-observation.service';

const facilityService = new FacilityService();

const ROWS_PER_PAGE = 25;

const useStyles = makeStyles((theme) => ({
  '.makeStyles-select .MuiInput-underline:before': {
    border: 'none !important',
  },
  actionCell: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
    borderTop: '1px solid #DEDEDE',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 16,
    padding: 10,
  },
  closeIcon: {
    '&:hover': {
      color: '#333',
      cursor: 'pointer',
    },
    color: '#aaa',
    display: 'none !important',
    padding: 2,
    position: 'absolute',
    right: 20,
    top: 4,
  },
  inputContainer: {
    '& select': {
      height: 30,
      width: 140,
    },
    '&:hover>.MuiSvgIcon-fontSizeSmall': {
      display: 'block !important',
    },
    position: 'relative',
  },
  list: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  menu: {
    border: '2px solid #E7E7E7',
    borderRadius: '5px',
    boxShadow: '2px 6px 10px rgba(0, 0, 0, 0.1)',
    paddingBottom: '1rem',
  },
  menuItem: {
    '&.MuiListItem-root.Mui-selected': {
      backgroundColor: 'white',
    },
    height: '40px',
  },
  noData: {
    alignItems: 'center',
    color: '#777',
    display: 'flex',
    flexDirection: 'column',
    fontSize: 20,
    fontWeight: 'bold',
    justifyContent: 'center',
    padding: 32,
    width: '100%',
  },
  pageContent: {
    marginBottom: '60px',
    width: '100%',
  },
  paginationContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    padding: '10px 0',
    position: 'fixed',
    right: 0,
    textAlign: 'center',
    width: '100%',
    zIndex: 10,
  },
  paper: { border: '1px solid #DEDEDE', borderRadius: '10px', marginTop: 20, padding: '0 10px', width: '100%' },
  root: { width: '100%' },
  searchInputRoot: {
    '& .MuiInput-input': {
      height: 30,
      padding: '0 5px',
    },
    '& .MuiInput-root': {
      height: 30,
    },
    height: 30,
  },
  select: {
    '& .MuiInputBase-input': {
      color: '#999',
      fontFamily: 'Roboto',
      fontSize: 13,
      height: '30px',
      padding: '0 5px',
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: '8px',
    color: '#999',
    height: '30px',
    overflow: 'hidden',
    width: '127px',
  },
  sortClearIcon: {
    '&:hover': {
      color: '#444',
      cursor: 'pointer',
    },
    color: '#999',
    height: '20px',
    marginLeft: '4px',
    width: '20px',
  },
  sortContainer: {
    alignItems: 'center',
    display: 'flex',
    marginLeft: 'auto',
  },
  sortIcon: {
    '&:hover': {
      color: '#444',
      cursor: 'pointer',
    },
    color: '#999',
    height: '24px',
    marginLeft: '8px',
    width: '24px',
  },
  sortSelect: {
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      cursor: 'pointer',
    },
    border: '1px solid #C8C8C8',
    borderRadius: 8,
    color: '#999',
    height: 40,
    padding: '0 16px',
    width: '170px !important',
  },
  table: { borderSpacing: '0 !important', minWidth: 750 },
  tableCell: {
    '&:hover': {
      cursor: 'default',
    },
    borderTop: '1px solid #DEDEDE',
    color: '#999999',
    fontSize: 14,
    fontWeight: 400,
    height: 48,
    lineHeight: 16,
    padding: '0px 6px !important',
  },
  tableCellClickable: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
    borderTop: '1px solid #DEDEDE',
    color: '#999999',
    fontSize: 14,
    fontWeight: 400,
    height: 48,
    lineHeight: 16,
    padding: '0px 6px !important',
  },
  tableRow: { '&.MuiTableRow-hover:hover': { cursor: 'pointer' } },
  userInput: {
    '&:focus': {
      borderColor: theme.palette.primary.main,
      color: '#444',
      outline: 'none',
    },
    border: '1px solid #C8C8C8',
    borderRadius: '8px',
    color: '#999',
    height: 40,
    padding: '0 16px',
    width: 240,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  worklistItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 20,
  },
}));

export default function WorklistContainer() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  // Parse URL query parameters
  const getInitialState = () => {
    const params = new URLSearchParams(location.search);
    return {
      department: params.get('department') || '',
      endDate: params.get('endDate') || '',
      facility: params.get('facility') || '',
      modality: params.get('modality') || '',
      order: params.get('sortOrder')?.toLowerCase() || 'desc',
      orderBy: params.get('sortBy') || 'createdAt',
      page: parseInt(params.get('page') || '1', 10) - 1,
      search: params.get('search') || '',
      startDate: params.get('startDate') || '',
      status:
        params.get('status') ||
        `${observationStatuses.new.value},${observationStatuses.in_progress.value},${observationStatuses.in_review.value},${observationStatuses.on_hold.value}`,
      type: params.get('type') || '',
    };
  };

  const initialState = getInitialState();

  const [orderBy, setOrderBy] = useState(initialState.orderBy);
  const [order, setOrder] = useState(initialState.order);
  const [page, setPage] = useState(initialState.page);
  const [loading, setLoading] = useState(false);
  const [qualityObservations, setQualityObservations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [selectedType, setSelectedType] = useState(initialState.type ? initialState.type.split(',') : []);
  const [selectedModality, setSelectedModality] = useState(initialState.modality ? initialState.modality.split(',') : []);
  const [selectedFacility, setSelectedFacility] = useState(initialState.facility || '');
  const [selectedDepartment, setSelectedDepartment] = useState(initialState.department || '');
  const [selectedStatus, setSelectedStatus] = useState(initialState.status ? initialState.status.split(',') : []);
  const [searchFilter, setSearchFilter] = useState(initialState.search || '');
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);

  // Facility and department data from API
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const { currentUser } = useAppContext();

  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();

    if (page > 0) params.set('page', String(page + 1));

    params.set('sortBy', orderBy);
    params.set('sortOrder', order.toUpperCase());

    // Add filter params if they exist
    if (selectedType && selectedType.length) params.set('type', selectedType.join(','));
    if (selectedModality && selectedModality.length) params.set('modality', selectedModality.join(','));
    if (selectedFacility) params.set('facility', selectedFacility);
    if (selectedDepartment) params.set('department', selectedDepartment);
    if (selectedStatus && selectedStatus.length) params.set('status', selectedStatus.join(','));
    if (searchFilter) params.set('search', searchFilter);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);

    // Update URL without reloading the page
    history.replace({ search: params.toString() });
  }, [history, page, orderBy, order, selectedType, selectedModality, selectedFacility, selectedDepartment, selectedStatus, searchFilter, startDate, endDate]);

  const buildQueryParams = useCallback(() => {
    const queryParams = {
      page: Math.max(1, page + 1),
      sortBy: orderBy,
      sortOrder: order.toUpperCase(),
    };

    if (selectedType && selectedType.length) queryParams.type = selectedType.join(',');
    if (selectedModality && selectedModality.length) queryParams.modality = selectedModality.join(',');
    if (selectedFacility) queryParams.facilityId = selectedFacility;
    if (selectedDepartment) queryParams.departmentId = selectedDepartment;
    if (selectedStatus && selectedStatus.length) queryParams.status = selectedStatus.join(',');
    if (searchFilter) queryParams.search = searchFilter;
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    return queryParams;
  }, [page, orderBy, order, selectedType, selectedModality, selectedFacility, selectedDepartment, selectedStatus, searchFilter, startDate, endDate]);

  const fetchQualityObservations = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = buildQueryParams();
      const response = await QualityObservationService.getAll(queryParams);

      if (response && response.items) {
        setQualityObservations(response.items);
        setTotalCount(response.total || response.items.length);
      }
    } catch (error) {
      console.error('Error fetching quality observations:', error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  const fetchFacilities = useCallback(async () => {
    try {
      setLoadingFacilities(true);

      const response = await facilityService.getAllFacilities();

      if (response) {
        setFacilities(response);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setLoadingFacilities(false);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      setLoadingDepartments(true);
      let response;

      if (selectedFacility) {
        response = await facilityService.getDepartments(selectedFacility);
      } else {
        response = await facilityService.getAllDepartments();
      }

      if (response) {
        setDepartments(response);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setLoadingDepartments(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  useEffect(() => {
    fetchQualityObservations();
  }, [fetchQualityObservations, page, orderBy, order, selectedType, selectedModality, selectedFacility, selectedDepartment, selectedStatus, startDate, endDate]);

  useWebSocketEvents({
    dependencies: [fetchQualityObservations],
    onEvent: (data) => {
      if (data.eventType === 'created' || data.eventType === 'updated') {
        fetchQualityObservations();
      }
    },
  });

  const handleSortFieldChange = (event) => {
    const newSortBy = event.target.value;
    setOrderBy(newSortBy);

    // When sorting by type, default to ascending (STAT > Call Back > Routine)
    if (newSortBy === 'type') {
      setOrder('asc');
    }
  };

  const toggleSortDirection = () => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
  };

  const handleChangePage = (event, newPage) => {
    setPage(Math.max(0, newPage - 1));
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    setPage(0);
  };

  const handleModalityChange = (value) => {
    setSelectedModality(value);
    setPage(0);
  };

  const handleFacilityChange = (event) => {
    setSelectedFacility(event.target.value);
    setSelectedDepartment('');
    setPage(0);
  };

  const resetFacilityFilter = () => {
    setSelectedFacility('');
    setPage(0);
  };

  const handleDepartmentChange = (event) => {
    setSelectedDepartment(event.target.value);
    setPage(0);
  };

  const resetDepartmentFilter = () => {
    setSelectedDepartment('');
    setPage(0);
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    setPage(0);
  };

  const handleRowClick = (item) => {
    if (currentUser?.role === 'technologist') {
      history.push(`/technologist-evaluation/${item.id}`);
    } else {
      history.push(`/observation/${item.id}`);
    }
  };

  const handleSearchChange = (value) => {
    setSearchFilter(value);
    setPage(0);
  };

  const handleSearch = (keyword) => {
    setSearchFilter(keyword);
    setPage(0);
    fetchQualityObservations();
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0);
  };

  const resetAllFilters = () => {
    // Clear all filter states
    setSelectedType([]);
    setSelectedModality([]);
    setSelectedFacility('');
    setSelectedDepartment('');
    setSelectedStatus([]);
    setSearchFilter('');
    setStartDate('');
    setEndDate('');
    setPage(0);

    history.replace({ search: '' });

    fetchQualityObservations();
  };

  const navigateToReportsPage = () => {
    const queryParams = new URLSearchParams();

    if (selectedType && selectedType.length > 0) queryParams.set('type', selectedType.join(','));
    if (selectedModality) queryParams.set('modality', selectedModality);
    if (selectedFacility) queryParams.set('facility', selectedFacility);
    if (selectedDepartment) queryParams.set('department', selectedDepartment);
    if (startDate) queryParams.set('startDate', startDate);
    if (endDate) queryParams.set('endDate', endDate);

    history.push({
      pathname: '/reports',
      search: queryParams.toString(),
    });
  };

  const isTechnologist = currentUser?.role === ROLES.TECHNOLOGIST.role;
  const isDevEnvironment = process.env.NODE_ENV === 'development';

  return (
    <div className={classes.root}>
      <Grid container style={{ padding: '40px 4rem' }} className={classes.pageContent}>
        <Grid item xs={12} style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', marginBottom: '20px', paddingLeft: '8px' }}>
          <Typography variant="h4" align="left" className="page-header">
            Worklist
          </Typography>

          {currentUser?.role !== 'technologist' && (
            <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
              <Button color="primary" variant="contained" style={{ height: '40px', width: '140px' }} onClick={navigateToReportsPage}>
                Run a report
              </Button>

              {isDevEnvironment && <CreateQualityObservationButton refreshWorklist={fetchQualityObservations} />}
              {isDevEnvironment && <WorkFeedbackButton />}
            </div>
          )}
        </Grid>

        <Box width="100%" display="flex" flexDirection="column" gridGap={14}>
          <Box className={classes.inputContainer} width="100%" display="flex" flexWrap="wrap" gridGap={10} alignItems="center">
            <Box width="566px">
              <SearchInput
                onSearch={handleSearch}
                onKeywordUpdate={handleSearchChange}
                placeholder="Search keyword..."
                disableSearch
                searchValue={searchFilter}
                classes={{
                  root: classes.searchInputRoot,
                }}
              />
            </Box>

            <DatePicker displayDate={startDate} onChange={(date) => handleStartDateChange(date)} defaultText="Start Date" />
            <DatePicker displayDate={endDate} onChange={(date) => handleEndDateChange(date)} defaultText="End Date" />
          </Box>

          <Box alignItems="center" width="100%" display="flex" flexWrap="wrap" gridGap={10}>
            <TypeMultiSelect selectedType={selectedType} onChange={handleTypeChange} className={classes.inputContainer} multiple />

            <ModalityMultiSelect selectedModality={selectedModality} onChange={handleModalityChange} className={classes.inputContainer} multiple />

            {currentUser?.role !== 'technologist' && (
              <div className={classes.inputContainer}>
                <select className={classes.select} id="facilitySelect" value={selectedFacility} onChange={handleFacilityChange}>
                  <option value="">Facility</option>
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
                {loadingFacilities && <LinearProgress style={{ height: 2, width: '100%' }} />}
                {!!selectedFacility && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetFacilityFilter} />}
              </div>
            )}

            {currentUser?.role !== 'technologist' && (
              <div className={classes.inputContainer}>
                <select className={classes.select} id="departmentSelect" value={selectedDepartment} onChange={handleDepartmentChange}>
                  <option value="">Department</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
                {loadingDepartments && <LinearProgress style={{ height: 2, width: '100%' }} />}
                {!!selectedDepartment && <Close fontSize="small" className={classes.closeIcon} title="Reset Value" onClick={resetDepartmentFilter} />}
              </div>
            )}

            <StatusMultiSelect selectedStatus={selectedStatus} onChange={handleStatusChange} className={classes.inputContainer} />

            <Box display="flex" gridGap={10}>
              <Button color="primary" variant="contained" style={{ height: 30, width: 124 }} onClick={fetchQualityObservations}>
                Search
              </Button>

              <Button color="primary" variant="outlined" startIcon={<Sync />} style={{ height: 30, width: 124 }} onClick={resetAllFilters}>
                Reset
              </Button>
            </Box>

            <div className={`${classes.sortContainer} ${classes.inputContainer}`}>
              <span className="mr-1">Sort By: </span>
              <select className={classes.sortSelect} id="sortSelect" value={orderBy} onChange={handleSortFieldChange}>
                <option value="patientName">Patient Name</option>
                <option value="examName">Exam Name</option>
                <option value="radiologistName">Radiologist Name</option>
                <option value="technologistName">Technologist Name</option>
                <option value="createdAt">Created</option>
                <option value="status">Status</option>
                <option value="type">Type</option>
                <option value="modality">Modality</option>
                <option value="facilityName">Facility Name</option>
                <option value="departmentName">Department Name</option>
              </select>

              <Tooltip title={order === 'asc' ? 'Ascending Order' : 'Descending Order'}>
                {order === 'asc' ? (
                  <ArrowUpward role="button" fontSize="small" className={classes.sortIcon} onClick={toggleSortDirection} />
                ) : (
                  <ArrowDownward role="button" fontSize="small" className={classes.sortIcon} onClick={toggleSortDirection} />
                )}
              </Tooltip>
            </div>
          </Box>
        </Box>

        <Box className={classes.worklistItems} width="100%">
          {qualityObservations.map((item) => {
            const isNew = !isTechnologist && !item.detailsOpened && item.status === observationStatuses.new.value;

            return <WorklistItem key={item.id} item={item} isNew={isNew} onClick={() => handleRowClick(item)} />;
          })}
        </Box>

        {qualityObservations.length === 0 && loading && (
          <div className={classes.noData}>
            Loading
            <LinearProgress color="primary" style={{ height: '2px', width: 'min(400px, 100%)' }} />
          </div>
        )}

        {qualityObservations.length === 0 && !loading && (
          <div className={classes.noData}>
            <DescriptionOutlined />
            No worklist items found
          </div>
        )}
      </Grid>

      {totalCount > 0 && Math.ceil(totalCount / ROWS_PER_PAGE) > 1 && (
        <Box className={classes.paginationContainer}>
          <Pagination className={classes.pagination} count={Math.ceil(totalCount / ROWS_PER_PAGE)} page={page + 1} onChange={handleChangePage} />
        </Box>
      )}
    </div>
  );
}
